/**
 * useNavigationConfig Hook
 * 
 * Manages navigation item visibility and order configuration with persistence
 */

import { useState, useEffect, useCallback } from 'react';
import { storage } from '@/utils/storage';
import { NavigationConfig, NavItemConfig, DEFAULT_NAVIGATION_CONFIG, DEFAULT_NAV_ITEMS } from '@/types/navigation';

const STORAGE_KEY = 'pp_navigation_config_v1';

export const useNavigationConfig = () => {
  const [config, setConfig] = useState<NavigationConfig>(() => {
    // Load from localStorage on init
    const saved = storage.get<NavigationConfig>(STORAGE_KEY, null);
    
    if (saved && saved.items) {
      // Merge with default items to handle new items that might have been added
      const mergedItems = DEFAULT_NAV_ITEMS.map((defaultItem, defaultIndex) => {
        const savedItem = saved.items.find(item => item.id === defaultItem.id);
        if (savedItem) {
          return savedItem;
        }
        // New item not in saved config
        return {
          ...defaultItem,
          visible: true,
          order: defaultIndex,
        };
      });
      
      // Sort by order
      mergedItems.sort((a, b) => a.order - b.order);
      
      return { items: mergedItems };
    }
    
    return DEFAULT_NAVIGATION_CONFIG;
  });

  // Save to localStorage whenever config changes
  useEffect(() => {
    storage.set(STORAGE_KEY, config);
  }, [config]);

  const updateConfig = useCallback((newConfig: NavigationConfig) => {
    setConfig(newConfig);
  }, []);

  const toggleItemVisibility = useCallback((itemId: string) => {
    setConfig((prev) => ({
      items: prev.items.map((item) =>
        item.id === itemId ? { ...item, visible: !item.visible } : item
      ),
    }));
  }, []);

  const reorderItems = useCallback((itemIds: string[]) => {
    setConfig((prev) => {
      const itemMap = new Map(prev.items.map(item => [item.id, item]));
      const reorderedItems = itemIds.map((id, index) => {
        const item = itemMap.get(id);
        if (item) {
          return { ...item, order: index };
        }
        return null;
      }).filter((item): item is NavItemConfig => item !== null);
      
      // Add any items that weren't in the reordered list
      const existingIds = new Set(itemIds);
      prev.items.forEach(item => {
        if (!existingIds.has(item.id)) {
          reorderedItems.push({ ...item, order: reorderedItems.length });
        }
      });
      
      return { items: reorderedItems };
    });
  }, []);

  const resetToDefault = useCallback(() => {
    setConfig(DEFAULT_NAVIGATION_CONFIG);
  }, []);

  // Get visible items sorted by order
  const getVisibleItems = useCallback(() => {
    return config.items
      .filter(item => item.visible)
      .sort((a, b) => a.order - b.order);
  }, [config]);

  return {
    config,
    visibleItems: getVisibleItems(),
    updateConfig,
    toggleItemVisibility,
    reorderItems,
    resetToDefault,
  };
};

